import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from 'styled-components';
import { Flex } from '@magnetic/flex';
import { pxToVh, pxToVw } from '../../../../common/util';
import { useTranslation } from 'react-i18next';
const responsiveLength = (px) => `clamp(${Math.max(Math.round(px * 0.68), 28)}px, min(${pxToVw(px)}, ${pxToVh(px)}), ${Math.round(px * 1.02)}px)`;
const responsiveCardWidth = (px) => `clamp(${Math.max(Math.round(px * 0.74), 76)}px, ${pxToVw(px)}, ${Math.round(px * 1.02)}px)`;
const responsiveCardHeight = (px) => `clamp(${Math.max(Math.round(px * 0.72), 72)}px, ${pxToVh(px)}, ${Math.round(px * 1.02)}px)`;
const responsiveRadius = (px) => `clamp(${Math.max(Math.round(px * 0.65), 4)}px, min(${pxToVw(px)}, ${pxToVh(px)}), ${Math.max(Math.round(px * 0.95), 6)}px)`;
const responsiveIconLength = (px) => `clamp(${Math.max(Math.round(px * 0.6), 10)}px, min(${pxToVw(px)}, ${pxToVh(px)}), ${Math.max(Math.round(px * 0.82), 14)}px)`;
const Container = styled(Flex) `
  background-color: #272a2f;
  background: #272a2f;
  height: ${(props) => responsiveCardHeight(props.height)};
  max-height: ${(props) => responsiveCardHeight(props.height)};
  width: ${(props) => responsiveCardWidth(props.width)};
  max-width: ${(props) => responsiveCardWidth(props.width)};
  border-radius: ${(props) => props.size === 'xs' || props.size === 'xsm'
    ? responsiveRadius(5)
    : responsiveRadius(8)};
  padding: 0.2vw;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
  box-shadow: 0px 2px 0px 0px rgba(52, 56, 62, 1) inset;
  border-image-source: linear-gradient(
    360deg,
    rgba(0, 0, 0, 0) 33.63%,
    #4c535b 100%
  );
`;
const Text = styled.span `
  font-family: 'Sora';
  font-size: ${(props) => FONT_SIZE[props.size]};
  font-size: ${(props) => pxToVw(FONT_SIZE[props.size])};
`;
const TitleText = styled(Text) `
  margin-bottom: 0.1vh;
  width: calc(100% - 0.5rem);
  max-width: 100%;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;
const StatusText = styled(Text) `
  color: ${(props) => (props.color ? props.color : '#00BCEB')};
  margin-top: -10px;
  max-width: calc(100% - 0.5rem);
  line-height: 1.15;
  overflow-wrap: anywhere;
  text-align: center;
`;
export const ImageContainer = styled.div `
  font-family: 'Sora';
  font-size: 20px;
  line-height: 2vw;
  min-height: 2vw;
  word-wrap: break-word;
`;
const STATUS_COLOR_MAPPING = {
    excellent: '#82CF5F',
    'very good': '#82CF5F',
    good: '#59A83D',
    fair: '#FFEC42',
    poor: '#FEAF1D',
    inadequate: '#FF5A36',
    number: '#00BCEB',
};
const TELEMETRY_IMG_MAPPING_FOR_TEMPERATURE = {
    0: require('../../../../assets/images/telemetry/10.png'),
    10: require('../../../../assets/images/telemetry/20.png'),
    20: require('../../../../assets/images/telemetry/50.png'),
    30: require('../../../../assets/images/telemetry/70.png'),
    40: require('../../../../assets/images/telemetry/70.png'),
    50: require('../../../../assets/images/telemetry/80.png'),
    60: require('../../../../assets/images/telemetry/80.png'),
    70: require('../../../../assets/images/telemetry/80.png'),
    80: require('../../../../assets/images/telemetry/90.png'),
    90: require('../../../../assets/images/telemetry/90.png'),
    100: require('../../../../assets/images/telemetry/100.png'),
};
const TELEMETRY_IMG_MAPPING = {
    excellent: require('../../../../assets/images/telemetry/excellent.png'),
    'very good': require('../../../../assets/images/telemetry/excellent.png'),
    good: require('../../../../assets/images/telemetry/good.png'),
    fair: require('../../../../assets/images/telemetry/fair.png'),
    poor: require('../../../../assets/images/telemetry/poor.png'),
    inadequate: require('../../../../assets/images/telemetry/inadequate.png'),
    10: require('../../../../assets/images/telemetry/10.png'),
    20: require('../../../../assets/images/telemetry/20.png'),
    30: require('../../../../assets/images/telemetry/30.png'),
    40: require('../../../../assets/images/telemetry/40.png'),
    50: require('../../../../assets/images/telemetry/50.png'),
    60: require('../../../../assets/images/telemetry/60.png'),
    70: require('../../../../assets/images/telemetry/70.png'),
    80: require('../../../../assets/images/telemetry/80.png'),
    90: require('../../../../assets/images/telemetry/90.png'),
    100: require('../../../../assets/images/telemetry/100.png'),
    'very low': require('../../../../assets/images/telemetry/20.png'),
    low: require('../../../../assets/images/telemetry/40.png'),
    busy: require('../../../../assets/images/telemetry/60.png'),
    'very busy': require('../../../../assets/images/telemetry/80.png'),
    crowded: require('../../../../assets/images/telemetry/100.png'),
    // added noise_prefix because 'good' already exists in this map
    noise_good: require('../../../../assets/images/telemetry/20.png'),
    noise_quiet: require('../../../../assets/images/telemetry/40.png'),
    noise_moderate: require('../../../../assets/images/telemetry/60.png'),
    noise_loud: require('../../../../assets/images/telemetry/80.png'),
    'noise_very loud': require('../../../../assets/images/telemetry/100.png'),
};
const TELEMETRY_ICON_MAPPING = {
    'Avg. Temperature': require('../../../../assets/images/telemetry/temperature_icon.svg'),
    'Avg. Humidity': require('../../../../assets/images/telemetry/humidity_icon.svg'),
    'Floor Occupancy': require('../../../../assets/images/telemetry/floor_occupancy_icon.svg'),
    'Ambient Noise': require('../../../../assets/images/telemetry/ambient_noise_icon.svg'),
};
const SIZE_MAP = {
    xs: 0.34,
    xsm: 0.42,
    sm: 0.7,
    md: 1,
    lg: 1.3,
    xl: 1.6,
};
const FONT_SIZE = {
    xs: 10,
    xsm: 12,
    sm: 19.6,
    md: 27.07,
    lg: 33,
    xl: 38,
};
function convertToCelsius(val) {
    return ((val - 32) * 5) / 9;
}
function getTelemetryImage(widgetType, statusValue, size, TELEMETRY_WIDGETS) {
    const badgeSize = 100 * SIZE_MAP[size];
    const iconSize = badgeSize * 0.18;
    const imageStyle = {
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        width: responsiveLength(badgeSize),
        height: responsiveLength(badgeSize),
        minWidth: responsiveLength(badgeSize),
        minHeight: responsiveLength(badgeSize),
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        marginBottom: `${pxToVh(10)}`,
        flex: '0 0 auto',
    };
    if (widgetType === TELEMETRY_WIDGETS.TEMPERATURE ||
        widgetType === TELEMETRY_WIDGETS.HUMIDITY ||
        widgetType === TELEMETRY_WIDGETS.OCCUPANCY ||
        widgetType === TELEMETRY_WIDGETS.AMBIENT_NOISE) {
        return (_jsx("div", Object.assign({ style: Object.assign(Object.assign({}, imageStyle), { backgroundImage: `url(${widgetType === TELEMETRY_WIDGETS.TEMPERATURE ? TELEMETRY_IMG_MAPPING_FOR_TEMPERATURE[statusValue] : TELEMETRY_IMG_MAPPING[statusValue]})` }) }, { children: _jsx("img", { src: TELEMETRY_ICON_MAPPING[widgetType], alt: "", "aria-hidden": "true", style: {
                    width: responsiveIconLength(iconSize),
                    height: responsiveIconLength(iconSize),
                    maxHeight: responsiveIconLength(iconSize),
                    maxWidth: responsiveIconLength(iconSize),
                } }) })));
    }
    else {
        return (_jsx("div", { style: Object.assign(Object.assign({}, imageStyle), { backgroundImage: `url(${TELEMETRY_IMG_MAPPING[statusValue]})` }) }));
    }
}
export function TelemetryWidget({ type, status, size = 'md', }) {
    function roundToNearestTen(num) {
        return Math.round(num / 10) * 10;
    }
    const { t } = useTranslation();
    const TELEMETRY_WIDGETS = {
        OCCUPANCY: 'Floor Occupancy',
        IAQ: 'Indoor Air Quality',
        CO2: 'CO2 Level',
        TEMPERATURE: 'Avg. Temperature',
        HUMIDITY: 'Avg. Humidity',
        AMBIENT_NOISE: 'Ambient Noise',
    };
    const localizedWidgetNames = {
        OCCUPANCY: t('common.floor-occupancy'),
        IAQ: t('common.iaq'),
        CO2: t('common.co2'),
        TEMPERATURE: t('common.temperature'),
        HUMIDITY: t('common.humidity'),
        AMBIENT_NOISE: t('common.ambient-noise'),
    };
    const widgetType = TELEMETRY_WIDGETS[type];
    let statusColor = STATUS_COLOR_MAPPING[status.toLowerCase()];
    let statusValue = status.toLowerCase();
    if (widgetType === TELEMETRY_WIDGETS.TEMPERATURE ||
        widgetType === TELEMETRY_WIDGETS.HUMIDITY) {
        let statusVCalc = status.substring(0, 2);
        const unit = status.split('°')[1];
        if (widgetType === TELEMETRY_WIDGETS.TEMPERATURE && unit === 'F') {
            statusVCalc = convertToCelsius(statusVCalc).toString();
        }
        statusValue = roundToNearestTen(parseInt(statusVCalc)).toString();
        statusColor = STATUS_COLOR_MAPPING['number'];
    }
    const localizedStatus = status.startsWith('noise_')
        ? t(`common.${status.substring(6).toLowerCase().replace(/ /g, '-')}`)
        : widgetType !== TELEMETRY_WIDGETS.TEMPERATURE &&
            widgetType !== TELEMETRY_WIDGETS.HUMIDITY
            ? t(`common.${status.toLowerCase().replace(/ /g, '-')}`)
            : status;
    return (_jsxs(Container, Object.assign({ direction: "verticle", gap: 4, height: 200 * SIZE_MAP[size], width: 280 * SIZE_MAP[size], align: "center", justify: "center", size: size, id: `telemetry-${widgetType}`, "aria-label": `${localizedWidgetNames[type]}: ${localizedStatus}`, style: { marginRight: '0.24vw' } }, { children: [_jsx(TitleText, Object.assign({ size: size, title: localizedWidgetNames[type] }, { children: localizedWidgetNames[type] })), _jsxs("div", Object.assign({ style: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0.1vw',
                    flexDirection: 'column',
                    marginTop: '-0.4vw',
                    width: '100%',
                } }, { children: [getTelemetryImage(widgetType, statusValue, size, TELEMETRY_WIDGETS), _jsx(StatusText, Object.assign({ size: size, color: statusColor, title: localizedStatus }, { children: localizedStatus }))] }))] })));
}
